import { v4 as uuidV4 } from 'uuid';
import {
  AirportLocationSchemaType,
  BaseTmdxAviaLegSchemaValues,
  CommonRouteLegSchemaValues,
  DEFAULT_AIRPORT_LOCATION_VALUES,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { fromDate, parseZonedDateTime, toTimeZone } from '@internationalized/date';
import {
  OPERATION_AT_TX_CENTER_DURATION,
  REFUEL_DURATION,
  SEGMENT_END_TIME_OFFSET,
  SWITCH_DURATION,
  SWITCH_DURATION_LOWER,
} from '@lp/features/RoutePlanner/config';
import { getLocationData } from '@lp/features/RoutePlanner/components/RouteLegForm/utils/locations';
import {
  AviaLegTypes,
  GroundLegTypes,
  LegLocationTypeEnum,
  RouteLegType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { getTzDateTimeString } from '@/utils/helpers';
import { CaseResourceType } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';
import { AVIA_LEG_TYPES, isAviaLegType, isGroundLegType } from '@/utils/route';

export const getValueBySegment = (
  segment: RouteSegmentType | null,
  isNewSegment: boolean = false,
): Partial<CommonRouteLegSchemaValues> => {
  const startDateNow = fromDate(new Date(), 'UTC')
    .set({ second: 0, millisecond: 0 })
    .add({ milliseconds: -OPERATION_AT_TX_CENTER_DURATION });

  const maxDateValue: Partial<CommonRouteLegSchemaValues> = {};

  const segmentValues = {
    segmentType: isNewSegment ? SegmentTypeEnum.custom : segment?.type,
    groupId: isNewSegment ? uuidV4() : segment?.id,
  };

  if (segment?.endTime) {
    maxDateValue.maxDate = parseZonedDateTime(segment.endTime)
      .add({ milliseconds: -SEGMENT_END_TIME_OFFSET })
      .toString();
  }

  if (isNewSegment || !segment || !segment.startTime) {
    const departureDateTimeValue = startDateNow.add({ milliseconds: OPERATION_AT_TX_CENTER_DURATION });
    return {
      departureDateTimeValue,
      departureDateTime: departureDateTimeValue.toString(),
      minDate: startDateNow.toString(),
      ...segmentValues,
      ...maxDateValue,
    };
  }

  return {
    departureDateTimeValue: parseZonedDateTime(getTzDateTimeString(segment.startTime)),
    departureDateTime: getTzDateTimeString(segment.startTime),
    minDate: getTzDateTimeString(segment.startTime),
    ...segmentValues,
    ...maxDateValue,
  };
};

export const getValuesByPrevGroundLeg = (
  logisticsId: string,
  prevLeg: GroundLegTypes,
  newLegType: RouteLegTypeEnum,
): Partial<CommonRouteLegSchemaValues> => {
  const isHospitals = [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter].includes(
    prevLeg.arrivalLocation.locationType,
  );

  const prevEndTime = getTzDateTimeString(prevLeg.arrivalTime);
  const groundSwitchDuration = isHospitals ? SWITCH_DURATION_LOWER : SWITCH_DURATION;
  const newStartDateTime = toTimeZone(parseZonedDateTime(prevEndTime), prevLeg.arrivalLocalTimezone).add({
    milliseconds: groundSwitchDuration,
  });
  const isCommercialLeg = newLegType === RouteLegTypeEnum.CommercialFlightAviaLeg;

  const location = getLocationData(logisticsId, prevLeg.legType, prevLeg.arrivalLocation);
  const isAirportLocation = location.locationType === LegLocationTypeEnum.airport;
  const departureAirportLocation = isAirportLocation && !isCommercialLeg ? location : DEFAULT_AIRPORT_LOCATION_VALUES;

  return {
    departureDateTimeValue: newStartDateTime,
    departureDateTime: newStartDateTime.toString(),
    departureLocalTimezone: location.timezone,
    departureLocation: AVIA_LEG_TYPES.includes(newLegType) ? departureAirportLocation : location,
  };
};

export const getValuesByPrevAviaLeg = (
  logisticsId: string,
  prevLeg: AviaLegTypes,
  newLegType: RouteLegTypeEnum,
): Partial<CommonRouteLegSchemaValues> => {
  const isPrevTmdxAvia = prevLeg.legType === RouteLegTypeEnum.TmdxJetAviaLeg;
  const isTmdxAvia = newLegType === RouteLegTypeEnum.TmdxJetAviaLeg;
  const isCommercialAvia = newLegType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const prevEndTime = getTzDateTimeString(prevLeg.arrivalTime);
  const switchDuration = isPrevTmdxAvia && isTmdxAvia ? REFUEL_DURATION : SWITCH_DURATION;
  const newStartDateTime = toTimeZone(parseZonedDateTime(prevEndTime), prevLeg.arrivalLocalTimezone).add({
    milliseconds: switchDuration,
  });

  const aviaDepartureLocation = (
    !isCommercialAvia && prevLeg.arrivalLocation.locationType === LegLocationTypeEnum.airport
      ? getLocationData(logisticsId, prevLeg.legType, prevLeg.arrivalLocation)
      : DEFAULT_AIRPORT_LOCATION_VALUES
  ) as AirportLocationSchemaType;

  return {
    departureDateTimeValue: newStartDateTime,
    departureDateTime: newStartDateTime.toString(),
    departureLocation: aviaDepartureLocation,
    departureLocalTimezone: isCommercialAvia ? prevLeg.arrivalLocalTimezone : aviaDepartureLocation.timezone,
  };
};

export const getValuesByPrevLeg = (
  logisticsId: string,
  prevLeg: RouteLegType | null,
  newLegType: RouteLegTypeEnum,
): Partial<CommonRouteLegSchemaValues> => {
  if (!prevLeg) {
    return {};
  }

  const isPrevAviaLegType = isAviaLegType(prevLeg);
  const isPrevGroundLegType = isGroundLegType(prevLeg);

  if (isPrevGroundLegType) {
    return getValuesByPrevGroundLeg(logisticsId, prevLeg, newLegType);
  }

  if (isPrevAviaLegType) {
    return getValuesByPrevAviaLeg(logisticsId, prevLeg, newLegType);
  }

  return {};
};

export const getDefaultAviaResource = (
  legs: (AviaLegTypes | GroundLegTypes)[] = [],
): Partial<BaseTmdxAviaLegSchemaValues> => {
  const tmdxAviaLegs = legs.findLast((leg) => leg.legType === RouteLegTypeEnum.TmdxJetAviaLeg);
  if (!tmdxAviaLegs) return {};

  const aircraftResources = tmdxAviaLegs.resources.filter((r) =>
    [CaseResourceType.Aircraft, CaseResourceType.Crew].includes(r.resourceType),
  );

  const aircraft = aircraftResources.find((r) => r.resourceType === CaseResourceType.Aircraft);

  if (!aircraft) {
    return {};
  }

  return {
    resources: aircraftResources,
    selectedAircraftId: aircraft.resourceId,
  };
};
